/**
 * Finder API endpoints, merged into the platform API client by the
 * extension mechanism (`apiEndpoints` export). Contracts live in the
 * backend extension (waraka_extensions.finder.api).
 */
export const finderApiEndpoints: Record<string, string> = {
  finderMyProfile: "GET:/finder/profile/me/",
  finderUpdateMyProfile: "PATCH:/finder/profile/me/",
  finderPublicProfile: "GET:/finder/profile/:handle/",
  finderDiscover: "GET:/finder/discover/",
  finderActivity: "GET:/finder/activity/",
  finderHideComment: "POST:/finder/comments/:pk/hide/",
  finderUnhideComment: "DELETE:/finder/comments/:pk/hide/",
  finderDeleteComment: "DELETE:/finder/comments/:pk/",
  finderEditComment: "PATCH:/finder/comments/:pk/",
  finderSaveAsk: "POST:/finder/asks/:pk/save/",
  finderUnsaveAsk: "DELETE:/finder/asks/:pk/save/",
  finderAddBookmark: "POST:/finder/bookmark/:handle/",
  finderRemoveBookmark: "DELETE:/finder/bookmark/:handle/",
  finderStats: "GET:/finder/stats/",
  finderSearch: "GET:/finder/search/",
  finderFeed: "GET:/finder/feed/",
  finderFollow: "POST:/finder/follow/:handle/",
  finderUnfollow: "DELETE:/finder/follow/:handle/",
  finderAskComments: "GET:/finder/asks/:pk/comments/",
  finderCreateComment: "POST:/finder/asks/:pk/comments/",
  finderConversations: "GET:/finder/messages/",
  finderThread: "GET:/finder/messages/:handle/",
  finderSendMessage: "POST:/finder/messages/:handle/",
  finderMyAnalytics: "GET:/finder/me/analytics/",
  finderAdminStats: "GET:/finder/admin/stats/",
  finderVerifyEmailStart: "POST:/finder/verify/institutional-email/start/",
  finderVerifyEmailConfirm: "POST:/finder/verify/institutional-email/confirm/",
  finderProfileLinks: "GET:/finder/profile/me/links/",
  finderCreateProfileLink: "POST:/finder/profile/me/links/",
  finderDeleteProfileLink: "DELETE:/finder/profile/me/links/:pk/",
  finderSendLinkVerify: "POST:/finder/profile/me/links/:pk/send-verify/",
  finderConfirmLinkEmail: "POST:/finder/profile/me/links/confirm/",
  finderUploadAvatar: "POST:/users/:pk/avatar/",
  finderDeleteAvatar: "DELETE:/users/:pk/avatar/",
  finderPortfolio: "GET:/finder/profile/me/portfolio/",
  finderCreatePortfolio: "POST:/finder/profile/me/portfolio/",
  finderUpdatePortfolio: "PATCH:/finder/profile/me/portfolio/:pk/",
  finderDeletePortfolio: "DELETE:/finder/profile/me/portfolio/:pk/",
  finderAffiliations: "GET:/finder/profile/me/affiliations/",
  finderCreateAffiliation: "POST:/finder/profile/me/affiliations/",
  finderUpdateAffiliation: "PATCH:/finder/profile/me/affiliations/:pk/",
  finderDeleteAffiliation: "DELETE:/finder/profile/me/affiliations/:pk/",
  finderMembership: "GET:/finder/membership/",
  finderApply: "POST:/finder/membership/apply/",
  finderAdminApplications: "GET:/finder/admin/applications/",
  finderAdminApplicationDecision: "POST:/finder/admin/applications/:pk/:action/",
  finderAdminBatchApprove: "POST:/finder/admin/applications/batch-approve/",
  finderAdminProfileSuspend: "POST:/finder/admin/profiles/:handle/:action/",
  finderAdminProfileRank: "POST:/finder/admin/profiles/:handle/rank/",
  finderProfileViews: "GET:/finder/profile/me/views/",
  finderMyEndorsements: "GET:/finder/profile/me/endorsements/",
  finderRefreshPublications: "POST:/finder/profile/me/publications/refresh/",
  finderPublicationVisibility: "POST:/finder/profile/me/publications/visibility/",
  finderPublicationManage: "POST:/finder/profile/me/publications/manage/",
  finderReport: "POST:/finder/report/",
  finderMyReports: "GET:/finder/reports/mine/",
  finderReportRespond: "POST:/finder/reports/:pk/respond/",
  finderAdminReports: "GET:/finder/admin/reports/",
  finderAdminReportResolve: "POST:/finder/admin/reports/:pk/:action/",
  finderAdminProfileModeration: "GET:/finder/admin/profiles/:handle/history/",
  finderAdminPeople: "GET:/finder/admin/people/",
  finderAdminOperators: "GET:/finder/admin/operators/",
  finderAdminOperatorAction: "POST:/finder/admin/operators/actions/",
  finderAdminOperatorActionResolve: "POST:/finder/admin/operators/actions/:pk/:action/",
  finderAsks: "GET:/finder/asks/",
  finderCreateAsk: "POST:/finder/asks/",
  finderAskDetail: "GET:/finder/asks/:pk/",
  finderUpdateAsk: "PATCH:/finder/asks/:pk/",
  finderRespondToAsk: "POST:/finder/asks/:pk/respond/",
  finderAskAddToProject: "POST:/finder/asks/:pk/responses/:responseId/add-to-project/",
  finderMyManagedProjects: "GET:/finder/my-managed-projects/",
  finderMyContributors: "GET:/finder/my-contributors/",
  finderInviteCandidates: "GET:/finder/projects/:projectId/invite-candidates/",
  finderSendInvites: "POST:/finder/projects/:projectId/invites/",
  finderRevokeInvite: "POST:/finder/projects/:projectId/invites/:pk/revoke/",
  finderMyInvites: "GET:/finder/invites/",
  finderRespondInvite: "POST:/finder/invites/:pk/:action/",
  finderReact: "POST:/finder/react/",
  finderOpenProjects: "GET:/finder/open-projects/",
  // The platform's own public-project feed. Making a project public is
  // how an AfriAnnotate owner asks for community help — "Discover" is
  // that surface, and the Finder board should show what is on it rather
  // than keeping a second, parallel list of open work.
  discoverProjects: "GET:/discover/",
  discoverJoin: "POST:/projects/:pk/join/",
  finderOpenProjectDetail: "GET:/finder/open-projects/:pk/",
  finderApplyToProject: "POST:/finder/open-projects/:pk/apply/",
  finderProjectFinderSettings: "GET:/finder/projects/:pk/finder-settings/",
  finderProjectFinderSettingsSave: "PATCH:/finder/projects/:pk/finder-settings/",
  finderProjectApplicants: "GET:/finder/projects/:pk/applicants/",
  finderProjectApplicantDecision: "POST:/finder/projects/:pk/applicants/:invite_pk/:action/",
  finderProfileVouches: "GET:/finder/profile/:handle/vouches/",
  finderVouch: "POST:/finder/vouch/:handle/",
  finderUnvouch: "DELETE:/finder/vouch/:handle/",
  finderNotifications: "GET:/finder/notifications/",
  // Member settings
  finderSettings: "GET:/finder/settings/",
  finderUpdateSettings: "PATCH:/finder/settings/",
  finderSettingsExport: "GET:/finder/settings/export/",
  finderDeactivate: "POST:/finder/settings/deactivate/",
  finderReactivate: "POST:/finder/settings/reactivate/",
  finderRequestDeletion: "POST:/finder/settings/delete-request/",
  finderCancelDeletion: "DELETE:/finder/settings/delete-request/",
  finderBlocks: "GET:/finder/blocks/",
  finderBlock: "POST:/finder/blocks/",
  finderUnblock: "DELETE:/finder/blocks/:handle/",
  finderMutes: "GET:/finder/mutes/",
  finderMute: "POST:/finder/mutes/",
  finderUnmute: "DELETE:/finder/mutes/:pk/",
  finderFeedPreview: "GET:/finder/feed/preview/",
  finderFeedbackSend: "POST:/finder/feedback/",
  finderFeedbackList: "GET:/finder/feedback/",
  finderFeedbackResolve: "PATCH:/finder/feedback/:pk/",
  finderFeedLess: "POST:/finder/feed/less/",
  finderFeedLessUndo: "POST:/finder/feed/less/undo/",
  finderProfileEndorsements: "GET:/finder/profile/:handle/endorsements/",
  finderEndorse: "POST:/finder/endorse/:handle/",
  finderUnendorse: "DELETE:/finder/endorse/:handle/",
  // Advisory topic suggestions. Answers with an empty list whenever
  // the model is not ready, which is the normal case today.
  finderTopicSuggest: "POST:/finder/topics/suggest/",
  finderTopicReadiness: "GET:/finder/topics/readiness/",
  finderCommunities: "GET:/finder/communities/",
  finderCommunityCreate: "POST:/finder/communities/",
  finderCommunityQuotaAsk: "POST:/finder/communities/quota/",
  finderCommunityQuotaSet: "PATCH:/finder/communities/quota/",
  finderCommunity: "GET:/finder/communities/:slug/",
  // One endpoint for joining, asking to join and accepting an invite:
  // all three are the member saying yes. DELETE is leaving, withdrawing
  // and declining, which are all the same row going away.
  finderCommunityJoin: "POST:/finder/communities/:slug/join/",
  finderCommunityLeave: "DELETE:/finder/communities/:slug/join/",
  finderCommunityInvite: "POST:/finder/communities/:slug/members/",
  finderCommunityRemove: "DELETE:/finder/communities/:slug/members/",
  // Scoped to the one community: it pauses posting, answering,
  // commenting and reacting THERE and nothing anywhere else.
  finderCommunityRestrict: "POST:/finder/communities/:slug/members/:handle/restrict/",
  finderCommunityUnrestrict: "DELETE:/finder/communities/:slug/members/:handle/restrict/",
};
